import { motion } from 'framer-motion'
import { useSound } from '../components/SoundHandler'
import { useResolution } from '../hooks/useResolution'
import { icons } from '../assets/icons/icons'
import { atom, useAtom } from 'jotai'
import { ReactNode } from 'react'
import { useAccount } from 'wagmi'
import { ContainerShape, HexagonShape, InnerShape } from '../components/Shape'
import { ProfileSlot } from '../components/ProfileSlot'

const atomTab = atom<'all' | 'toys' | 'merch'>('all')

export const ShopModal = ({ onDismiss }: { onDismiss: () => void }) => {
  const sound = useSound();
  const { isMobile } = useResolution()
  const { address } = useAccount()

  const [tab, setTab] = useAtom(atomTab)

  const onClose = () => {
    onDismiss()
    sound.play('click');
  }

  const ui = {
  }

  return (
    <div className="fixed z-30 inset-0">
      <motion.div
        className="absolute inset-0 bg-[#0a0523]/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      <motion.div
        className="absolute inset-0"
        initial={{ backdropFilter: 'blur(20px) opacity(0)' }}
        animate={{ backdropFilter: 'blur(20px) opacity(1)' }}
      />

      <div className='flex flex-col gap-4 md:gap-[45px] px-2 md:px-0 pt-6 pb-4 md:py-10 max-w-[1280px] mx-auto h-full'>
        {isMobile ? (
          <div className="relative">
            <motion.div
              className="flex flex-col items-center justify-center gap-1 py-2.5"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <p className="text-[24px] font-blinko text-center text-white">
                Shop
              </p>
            </motion.div>
            
            <button className='absolute top-0 right-2' onClick={onClose}>
              <HexagonShape cornerRadius={6} strokeWidth={1}>
                <div className="flex justify-center items-center p-2">
                  <img src={icons.close} className='w-6 h-6' />
                </div>
              </HexagonShape>
            </button>
          </div>
        ) : (
          <div className="relative">
            <motion.div
              className="flex flex-col items-center justify-center gap-8"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <p className="text-[48px] font-blinko text-center text-white">
                Shop
              </p>
            </motion.div>
            
            <button className='absolute top-0 left-0' onClick={onClose}>
              <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                <div className="px-8 py-3">
                  <p className="text-base font-blinko text-white">
                    Back to Game
                  </p>
                </div>
              </InnerShape>
            </button>
          </div>
        )}

        <div className='flex-1 overflow-hidden'>
          <ContainerShape {...(
              isMobile 
                ? {cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 } 
                : {cornerDiagonal: 60, cornerRadius: 25, strokeWidth: 2 })
          }>
            <div className='flex flex-col h-full py-6 px-3 md:px-12 md:py-12'>
              <div className='flex justify-between items-center mb-3'>
                <div className="flex pr-10 overflow-x-auto whitespace-nowrap">
                  <TabButton active={tab === 'all'} onClick={() => setTab('all')}>All</TabButton>
                  <TabButton active={tab === 'toys'} onClick={() => setTab('toys')}>Toys</TabButton>
                  <TabButton active={tab === 'merch'} onClick={() => setTab('merch')}>Merch</TabButton>
                </div>

                <ProfileSlot address={address}>
                  {({ profile }) => (
                    <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                      <div className='flex justify-center items-center gap-2 px-4 py-2'>
                        <img src={icons.jars} className='w-4 h-4' />
                        <p className="font-blinko text-sm text-white">{profile.totalHoney}</p>
                      </div>
                    </InnerShape>
                  )}
                </ProfileSlot>
              </div>

              <div className='flex-1 flex items-center justify-center px-12 pb-12'>
                <p className='text-xl md:text-2xl font-blinko text-white'>Coming Soon</p>
              </div>
            </div>
          </ContainerShape>
        </div>
      </div>
    </div>
  )
};

const TabButton = ({ children, active, onClick }: { children: ReactNode, active: boolean, onClick: () => void }) => {
  const sound = useSound();
  if (active) {
    return (
      <button onClick={() => {
        sound.play('click');
        onClick();
      }}>
        <InnerShape cornerDiagonal={12} cornerRadius={6} strokeWidth={1}>
          <div className="px-4 py-2">
            <p className="text-sm text-white font-blinko">{children}</p>
          </div>
        </InnerShape>
      </button>
    )
  }

  return (
    <button className="px-4 py-2 rounded-xl" onClick={() => {
      sound.play('click');
      onClick();
    }}>
      <p className="text-sm text-white/80 font-blinko">{children}</p>
    </button>
  )
}
